import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateFilter from 'components/field/DateFilter';
import DateInput from 'components/field/DateInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';
import useListExport from 'components/list/useListExport';

import useAppTracking from 'hooks/useAppTracking';
import { usePrompt } from 'hooks/usePrompt';

import { DeliveryAttemptReportListConfig } from 'pages/Shipments/Operation/DeliveryAttemptReport/DeliveryAttemptReportList';
import { PickupDeliveryReportOperationConfig } from 'pages/Shipments/Operation/PickupDeliveryReportOperation/PickupDeliveryReportOperation';
import { TimeStatsForOperationsConfig } from 'pages/Shipments/Operation/TimeStatsForOperation/TimeStatsForOperationList';

import { AUTOCOMPLETE } from 'services/consts';

import PATHS from 'utils/paths';

import { faEye, faFileExcel } from '@fortawesome/pro-regular-svg-icons';

import { BranchDailyStaffReportConfig } from '../BranchReports/BranchDailyStaffReport/BranchDailyStaffReport';
import { BranchReportConfig } from '../BranchReports/BranchReport/BranchReport';
import { DriverSummaryConfig } from '../OTOFlexReports/DriverSummaryReport/DriverSummaryReport';
import { TransactionReportConfig } from '../OTOFlexReports/TransactionReport/TransactionReport';
import { DailyOrderStatsConfig } from '../OrderReports/DailyOrderStats/DailyOrderStats';
import { OrderStatusLogsConfig } from '../OrderReports/OrderStatusLogs/OrderStatusLogs';
import { SalesBySKUConfig } from '../OrderReports/SalesBySKU/SalesBySKU';
import { TimeStatsReportConfig } from '../OrderReports/TimeStatsReports/TimeStatsReport';
import { AggregatorProfitReportConfig } from '../ShipmentReports/AggregatorProfitReport/AggregatorProfitReport';
import { DeliveredOrdersByCityByCourierConfig } from '../ShipmentReports/DeliveredOrdersByCityByCourier/DeliveredOrdersByCityByCourier';
import { DeliveryCompanyReportByCityConfig } from '../ShipmentReports/DeliveryCompanyReportByCity/DeliveryCompanyReportByCity';

/**
 * @author Burak AK
 */

const getEventObj = (name, dateFilter) => {
	const obj = {
		name: 'reportsGenerated',
		data: { name, dateStart: '', dateEnd: '' }
	};
	if (dateFilter) {
		try {
			obj.data.dateStart = dateFilter.start;
			obj.data.dateEnd = dateFilter.end;
		} catch {
			// do nothing
		}
	}
	return obj;
};

const ReportExportConfig = {
	// OrderReports
	[PATHS.ORDER_STATUS_LOGS]: {
		configName: OrderStatusLogsConfig,
		defaultFilters: [
			{
				column: OrderStatusLogsConfig.columns.find((col) => col.accessor === 'date'),
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	[PATHS.DAILY_ORDER_STATS]: {
		configName: DailyOrderStatsConfig,
		defaultFilters: [
			{
				column: DailyOrderStatsConfig.columns.find((col) => col.accessor === 'transactionDate'),
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},

	[PATHS.TIME_STATS]: {
		configName: TimeStatsReportConfig,
		defaultFilters: [
			{
				column: TimeStatsReportConfig.columns.find((col) => col.accessor === 'newOrderDate'),
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	[PATHS.TIME_STATS_FOR_OPERATION]: {
		configName: TimeStatsForOperationsConfig,
		defaultFilters: [
			{
				column: TimeStatsForOperationsConfig.columns.find((col) => col.accessor === 'shipmentCreatedDate'),
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	[PATHS.SALES_BY_SKU]: {
		configName: SalesBySKUConfig,
		defaultFilters: [
			{
				column: {
					Header: 'orderDate',
					accessor: 'createdAt',
					filterType: FilterType.date,
					filterRemovable: false,
					customFilterOptions: [FilterAction.between, FilterAction.on]
				},
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	// ShipmentReports
	[PATHS.DELIVERY_COMPANY_REPORT_BY_CITY]: {
		configName: DeliveryCompanyReportByCityConfig,
		defaultFilters: [
			{
				column: {
					Header: 'orderDate',
					accessor: 'createdAt',
					filterType: FilterType.date,
					filterRemovable: false,
					customFilterOptions: [FilterAction.between, FilterAction.on]
				},
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	[PATHS.PICKUP_DELIVERY_REPORT_LIST]: {
		configName: PickupDeliveryReportOperationConfig,
		defaultFilters: [
			{
				column: PickupDeliveryReportOperationConfig.columns.find((col) => col.accessor === 'clientName'),
				action: FilterAction.is,
				value: 'clientName'
			}
		]
	},
	[PATHS.DELIVERY_ATTEMPT_REPORT_FOR_OPERATION]: {
		configName: DeliveryAttemptReportListConfig,
		defaultFilters: [
			{
				column: DeliveryAttemptReportListConfig.columns.find((col) => col.accessor === 'newOrderDate'),
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	[PATHS.DELIVERED_ORDERS_BY_CITY_BY_COURIER]: {
		configName: DeliveredOrdersByCityByCourierConfig,
		defaultFilters: [
			{
				column: {
					Header: 'orderDate',
					accessor: 'createdAt',
					filterType: FilterType.date,
					filerRemovable: false,
					customFilterOptions: [FilterAction.between, FilterAction.on]
				},
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			},
			{
				column: DeliveredOrdersByCityByCourierConfig.columns[1],
				action: FilterAction.is,
				value: 'deliveryCompanyName'
			}
		]
	},
	[PATHS.AGGREGATOR_PROFIT_REPORT]: {
		configName: AggregatorProfitReportConfig,
		defaultFilters: [
			{
				column: {
					Header: 'pickUpDate',
					accessor: 'pickUpDate',
					filterType: FilterType.date,
					filterRemovable: false,
					customFilterOptions: [FilterAction.between, FilterAction.on]
				},
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},

	// OTOFlexReports
	[PATHS.DRIVER_SUMMARY]: {
		configName: DriverSummaryConfig,
		defaultFilters: [
			{
				column: {
					Header: 'orderDate',
					accessor: 'createdAt',
					filterType: FilterType.date,
					filerRemovable: false,
					customFilterOptions: [FilterAction.between, FilterAction.on]
				},
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	[PATHS.TRANSACTION_REPORT]: {
		configName: TransactionReportConfig,
		defaultFilters: [
			{
				column: TransactionReportConfig.columns.find((col) => col.accessor === 'createdDate'),
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	// BranchReports
	[PATHS.BRANCH_REPORT]: {
		configName: BranchReportConfig,
		defaultFilters: [
			{
				column: BranchReportConfig.columns.find((col) => col.accessor === 'createdAt'),
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	},
	[PATHS.BRANCH_DAILY_STAFF_REPORT]: {
		configName: BranchDailyStaffReportConfig,
		defaultFilters: [
			{
				column: BranchDailyStaffReportConfig.columns.find((col) => col.accessor === 'dateOfShipment'),
				action: FilterAction.between,
				value: {
					start: 'start',
					end: 'end'
				}
			}
		]
	}
};

export const ReportsPreFilterModal = NiceModal.create(({ actionPath, filterArray, title }) => {
	const modal = useModal();
	const navigate = useNavigate();
	const handleListExport = useListExport();
	const { track } = useAppTracking();
	const defaultColSpan = {
		md: 6
	};

	const matchingConfig = useMemo(() => ReportExportConfig[actionPath]);

	const isExporting = useSelector((state) => state.dynamicListData.isExporting);

	useEffect(() => {
		filterArray.forEach((filterItem) => {
			if (filterItem?.component === 'dateFilter') {
				setValue(
					filterItem.name,
					{
						start: dayjs().subtract(1, 'day').endOf('day').toISOString(),
						end: dayjs().endOf('day').toISOString()
					},
					{ shouldValidate: true, shouldDirty: true }
				);
			}
		});
	}, [filterArray]);

	// The schema is created according to the required fields in the filterArray
	const schemaShape = {};
	filterArray.forEach((filterItem) => {
		const { name, required, component } = filterItem;
		switch (component) {
			case 'dateFilter':
			case 'dateInput':
				if (required) {
					schemaShape[name] = yup.object().nullable().required();
				} else {
					schemaShape[name] = yup.object().nullable();
				}
				break;
			case 'autoComplete':
				if (required) {
					schemaShape[name] = yup.number().nullable().required();
				} else {
					schemaShape[name] = yup.number().nullable();
				}
				break;
			case 'stringInput':
				if (required) {
					schemaShape[name] = yup.string().nullable().required();
				} else {
					schemaShape[name] = yup.string().nullable();
				}
				break;

			default:
				break;
		}
	});

	const schema = useMemo(
		() =>
			yup.object().shape({
				...schemaShape
			}),
		[filterArray]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema)
	});

	const {
		watch,
		setValue,
		handleSubmit,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	// With componentArray, the component is created according to the required fields by returning the filterArray
	const componentArray = filterArray
		.map((filterItem) => {
			const {
				component,
				name,
				label,
				required,
				selectionLabelAccessor,
				autoCompleteName,
				autoCompleteStringHolder
			} = filterItem;

			// AutoComplete columns settings
			const autocompleteColumns = useMemo(() => [{ accessor: selectionLabelAccessor }], []);

			if (component === 'dateInput') {
				return {
					component: (
						<FormItem colSpan={defaultColSpan} label={label} required={required}>
							<DateInput
								name={name}
								value={watch(name)}
								setValue={(value) => {
									setValue(name, value, { shouldValidate: true, shouldDirty: true });
								}}
								rangeMode
							/>
						</FormItem>
					)
				};
			}
			if (component === 'dateFilter') {
				return {
					component: (
						<FormItem colSpan={defaultColSpan} label={label} required={required}>
							<DateFilter
								defaultDay="today"
								setValue={(value) => {
									setValue(name, value, { shouldValidate: true, shouldDirty: true });
								}}
							/>
						</FormItem>
					)
				};
			}
			if (component === 'stringInput') {
				return {
					component: (
						<FormItem colSpan={defaultColSpan} label={label} required={required}>
							<StringInput name={name} />
						</FormItem>
					)
				};
			}

			if (component === 'autoComplete') {
				return {
					component: (
						<FormItem colSpan={defaultColSpan} label={label} required={required}>
							<AutoComplete
								autoCompleteName={AUTOCOMPLETE[autoCompleteName]}
								columns={autocompleteColumns}
								selectionLabelAccessor={selectionLabelAccessor}
								value={watch(name)}
								name={name}
								setValue={(value) => {
									setValue(name, value?.ID ?? value?.id ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});

									setValue(autoCompleteStringHolder, value?.Name ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
								}}
							/>
						</FormItem>
					)
				};
			}

			return null;
		})
		.filter((item) => item !== null);

	const handleCloseModal = () => {
		modal.remove();
	};

	const onSubmit = (data) => {
		const eventObj = getEventObj(title, data?.date ? data?.date : null);
		track(eventObj.name, eventObj.data, ['mixpanel']);
		navigate(actionPath, { state: { data } });
		handleCloseModal();
	};

	const onExport = async (data) => {
		const defaultFiltersValue = [];

		matchingConfig.defaultFilters.forEach((filter) => {
			let filterValue = null;

			if (filter.action === FilterAction.between) {
				filterValue = {
					start: data.date[filter.value.start],
					end: data.date[filter.value.end]
				};
			} else if (filter.action === FilterAction.is) {
				filterValue = data[filter.value];
			}

			defaultFiltersValue.push({
				column: filter.column,
				action: filter.action,
				value: filterValue
			});
		});
		const dateFilter = defaultFiltersValue.find((filter) => filter.action === 'between');
		const eventObj = getEventObj(title, dateFilter ? dateFilter?.value : null);
		track(eventObj.name, eventObj.data, ['mixpanel']);
		const success = await handleListExport({
			...matchingConfig.configName,
			defaultFilters: defaultFiltersValue
		});
		if (success) {
			handleCloseModal();
		}
	};

	return (
		<>
			<Modal maxWidth="small" fullwidth className="modal-reportsPreFilterModal">
				<ModalTitle>
					<Typography variant="p1">{title}</Typography>
				</ModalTitle>
				<ModalContent>
					<Form formMethods={formMethods}>
						{componentArray.map((item, index) => (
							<React.Fragment key={index}>{item.component}</React.Fragment>
						))}
					</Form>
				</ModalContent>
				<ModalActions>
					<Button
						onClick={() => {
							handleCloseModal();
						}}
						label="cancel"
						variant="outline"
						style={{ minWidth: '70px' }}
					/>

					<Button
						onClick={handleSubmit(onExport)}
						icon={faFileExcel}
						color="var(--green)"
						label="exportData"
						loading={isExporting}
						loadingTooltip={isExporting && 'formInfo.alreadyYourReportExporting'}
						style={{ minWidth: '70px' }}
					/>

					<Button
						onClick={handleSubmit(onSubmit)}
						icon={faEye}
						label="viewData"
						style={{ minWidth: '70px' }}
					/>
				</ModalActions>
			</Modal>
		</>
	);
});

export default ReportsPreFilterModal;
